import { type FormEvent, useEffect, useState } from 'react';
import { submitWebsiteLead } from '../../api';
import WebsiteLibrary from '../WebsiteLibrary/WebsiteLibrary';
import { websiteProjects } from '../WebsiteLibrary/projects';

type FormState = 'idle' | 'sending' | 'sent' | 'failed';

const serviceFeatures = [
  {
    number: '01',
    title: 'Designed around your brand',
    body: 'Your colors, voice, photography, services, and goals shape the final site. The starting system makes delivery faster—not generic.',
  },
  {
    number: '02',
    title: 'A dashboard that belongs to you',
    body: 'Update text, images, links, services, pricing, and key sections without waiting on a developer or paying a per-edit fee.',
  },
  {
    number: '03',
    title: 'Launch support included',
    body: 'CMJ Software handles responsive polish, forms, domain connection, launch checks, and a guided handoff for your dashboard.',
  },
  {
    number: '04',
    title: 'Ready for what comes next',
    body: 'Add booking, payments, products, memberships, integrations, or new pages when your business is ready to grow.',
  },
];

const buildPaths = [
  {
    label: 'Launch',
    title: 'A focused site that looks established.',
    description: 'For a new business, event, professional, or campaign that needs a polished place to send people.',
    features: ['Focused 1–3 page experience', 'Responsive custom design', 'Personal editing dashboard', 'Lead or contact form'],
    cta: 'Plan my launch',
  },
  {
    label: 'Business',
    title: 'Your complete digital front door.',
    description: 'For an active business that needs richer content, better conversion, and room to keep growing.',
    features: ['Custom multi-page website', 'Advanced content dashboard', 'Forms and enquiry management', 'Analytics and launch support'],
    cta: 'Build my business site',
    featured: true,
  },
  {
    label: 'Commerce+',
    title: 'A website that does the work.',
    description: 'For stores, booking flows, memberships, portals, and business-specific digital experiences.',
    features: ['Payments or booking', 'Products or member content', 'Custom workflows and integrations', 'Ongoing growth options'],
    cta: 'Scope my platform',
  },
];

const processSteps = [
  ['Discover', 'A focused call aligns the audience, goals, content, features, and right build path.'],
  ['Design', 'CMJ shapes the visual direction and builds the real responsive experience around your brand.'],
  ['Refine', 'You review the site, we polish the details, and your content is prepared for launch.'],
  ['Own it', 'Your site launches with dashboard access, training, and unlimited self-service content editing.'],
] as const;

const faqItems = [
  {
    question: 'What does unlimited editing mean?',
    answer: 'After launch, you can make unlimited self-service content changes through your dashboard—text, images, links, services, pricing, and the editable sections included in your build. There is no per-edit charge.',
  },
  {
    question: 'Is this a pre-made template?',
    answer: 'It is a proven website system customized for your brand and goals. We use reliable building blocks to move faster, then tailor the art direction, content, page structure, and dashboard to the business.',
  },
  {
    question: 'Can CMJ Software make changes for me?',
    answer: 'Yes. Your dashboard covers everyday content updates. New features, redesigns, integrations, and done-for-you updates can be scoped as additional web development.',
  },
  {
    question: 'How do payment and pricing work?',
    answer: 'Every build receives a written scope and fixed quote before work begins. Approved web development can then be paid online securely. No surprise work is added without written approval.',
  },
];

export function TemplateProjectPage({ projectSlug }: { projectSlug: string }) {
  const destination = `/websites/${encodeURIComponent(projectSlug)}${typeof window === 'undefined' ? '' : window.location.search}`;
  useEffect(() => { window.location.replace(destination); }, [destination]);
  return <main className="website-library"><div className="wl-shell wl-not-found"><h1>Opening the project collection.</h1><p><a href={destination}>Continue to the website project →</a></p></div></main>;
}

export function TemplatesPage() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Templates as a Service | CMJ Software';
    // Cross-page enquiry links arrive before React has mounted the target section.
    const anchorFrame = window.requestAnimationFrame(() => {
      const anchor = window.location.hash.slice(1);
      if (anchor) document.getElementById(anchor)?.scrollIntoView({ block: 'start' });
    });
    return () => {
      window.cancelAnimationFrame(anchorFrame);
      document.title = previousTitle;
    };
  }, []);

  async function handleInquiry(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormState('sending');
    setFormMessage('');

    const form = event.currentTarget;
    const data = new FormData(form);
    const buildType = String(data.get('buildType') || 'Not sure yet');
    const timeline = String(data.get('timeline') || 'Flexible');
    const projectMessage = String(data.get('message') || '');
    const result = await submitWebsiteLead({
      requestType: 'contact',
      name: String(data.get('name') || ''),
      email: String(data.get('email') || ''),
      phone: String(data.get('phone') || ''),
      company: String(data.get('company') || ''),
      interest: `Templates as a Service — ${buildType}`,
      teamSize: timeline,
      message: projectMessage,
    });

    setFormState(result.ok ? 'sent' : 'failed');
    setFormMessage(result.message);
    if (result.ok) form.reset();
  }

  return (
    <main className="templatesSite" id="templates-top">
      <header className="templatesHeader">
        <a className="templatesBrand" href="/templates" aria-label="CMJ Software Templates as a Service home">
          <img src="/cmj-software-logo.png" alt="" />
          <span>
            <strong>CMJ Software</strong>
            <small>Templates as a Service</small>
          </span>
        </a>
        <nav aria-label="Templates page navigation">
          <a href="#templates-work">Work</a>
          <a href="#templates-dashboard">Dashboard</a>
          <a href="#templates-plans">Plans</a>
          <a href="#templates-faq">FAQ</a>
        </nav>
        <a className="templatesHeaderCta" href="#templates-contact">Start a project <span aria-hidden="true">↗</span></a>
      </header>

      <section className="templatesHero" aria-labelledby="templates-title">
        <div className="templatesHeroGlow" aria-hidden="true" />
        <div className="templatesShell templatesHeroCopy">
          <p className="templatesKicker"><span /> Websites, built with you</p>
          <h1 id="templates-title">A website built for you.<br /><em>A dashboard that stays yours.</em></h1>
          <p className="templatesHeroText">
            Get a polished, custom website without the drawn-out agency process—then keep it fresh with unlimited editing from your own private dashboard.
          </p>
          <div className="templatesHeroActions">
            <a className="templatesButton templatesButtonDark" href="#templates-contact">Build my website <span aria-hidden="true">↗</span></a>
            <a className="templatesTextLink" href="#templates-work">Explore the work <span aria-hidden="true">↓</span></a>
          </div>
          <div className="templatesPromise" aria-label="Service highlights">
            <span><i aria-hidden="true">✓</i> Custom launch</span>
            <span><i aria-hidden="true">✓</i> Personal dashboard</span>
            <span><i aria-hidden="true">✓</i> Unlimited content edits</span>
          </div>
        </div>

        <div className="templatesHeroStage" aria-label="A selection of websites created by CMJ Software">
          <div className="templatesStageCard templatesStageBack">
            <div className="templatesBrowserBar"><span /><span /><span /></div>
            <img src="/templates-assets/beauty-by-brittany-website.jpg" alt="Beauty by Brittany website" />
          </div>
          <div className="templatesStageCard templatesStageMain">
            <div className="templatesBrowserBar"><span /><span /><span /><small>yourbusiness.com</small></div>
            <img src="/templates-assets/glec-website.jpg" alt="Great Lakes Estate Cleanouts website" />
          </div>
          <div className="templatesStageCard templatesStageFront">
            <div className="templatesBrowserBar"><span /><span /><span /></div>
            <img src="/templates-assets/eyala-website.jpg" alt="Eyala website" />
          </div>
        </div>
      </section>

      <section className="templatesStatement">
        <div className="templatesShell templatesStatementGrid">
          <p className="templatesSectionLabel">The service</p>
          <div>
            <h2>Launch faster. Look original.<br />Stay in control.</h2>
            <p>Templates as a Service combines CMJ Software’s proven design systems with the attention of a custom build. You get a distinct website, a simpler process, and a dashboard made for everyday updates.</p>
          </div>
        </div>
        <div className="templatesShell templatesFeatureGrid">
          {serviceFeatures.map((feature) => (
            <article key={feature.number}>
              <span>{feature.number}</span>
              <h3>{feature.title}</h3>
              <p>{feature.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="templates-work" aria-label="Explore website projects">
        <WebsiteLibrary projects={websiteProjects} theme="light" compact basePath="/websites" inquiryHref="#templates-contact" brandHref="/" logoSrc="/cmj-software-logo.png" />
      </section>

      <section className="templatesDashboard" id="templates-dashboard" aria-labelledby="templates-dashboard-title">
        <div className="templatesShell templatesDashboardGrid">
          <div className="templatesDashboardCopy">
            <p className="templatesSectionLabel">Your private workspace</p>
            <h2 id="templates-dashboard-title">The website is only half the product.</h2>
            <p>Your dashboard turns everyday updates into a simple business task. Change what customers see, review enquiries, preview the result, and publish when you are ready.</p>
            <ul>
              <li><span>01</span><div><strong>Edit your content</strong><small>Keep offers, services, images, and details current.</small></div></li>
              <li><span>02</span><div><strong>See incoming enquiries</strong><small>Keep new customer conversations within reach.</small></div></li>
              <li><span>03</span><div><strong>Preview before publishing</strong><small>Make changes confidently without touching code.</small></div></li>
            </ul>
          </div>
          <div className="templatesDashboardVisual">
            <div className="templatesDashboardHalo" aria-hidden="true" />
            <div className="templatesBrowserFrame templatesDashboardFrame">
              <div className="templatesBrowserBar"><span /><span /><span /><small>My website dashboard</small></div>
              <img src="/templates-assets/glec-dashboard.jpg" alt="Great Lakes website owner dashboard with content and enquiry controls" />
            </div>
            <div className="templatesDashboardBadge">
              <span aria-hidden="true">∞</span>
              <div><strong>Unlimited editing</strong><small>No per-edit fees</small></div>
            </div>
          </div>
        </div>
      </section>

      <section className="templatesProcess" aria-labelledby="templates-process-title">
        <div className="templatesShell">
          <div className="templatesSectionHeading templatesSectionHeadingLight">
            <div>
              <p className="templatesSectionLabel">How it works</p>
              <h2 id="templates-process-title">From idea to live website.<br /><em>Without the usual drag.</em></h2>
            </div>
            <p>A clear four-part process keeps decisions focused, progress visible, and your launch moving.</p>
          </div>
          <div className="templatesProcessGrid">
            {processSteps.map(([title, body], index) => (
              <article key={title}>
                <span>0{index + 1}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="templatesPlans" id="templates-plans" aria-labelledby="templates-plans-title">
        <div className="templatesShell templatesSectionHeading">
          <div>
            <p className="templatesSectionLabel">Choose your build path</p>
            <h2 id="templates-plans-title">The right amount of website.<br /><em>Room to grow later.</em></h2>
          </div>
          <p>Every project receives a clear written scope and fixed quote. Choose the closest fit and CMJ Software will shape the details with you.</p>
        </div>
        <div className="templatesShell templatesPlanGrid">
          {buildPaths.map((plan) => (
            <article className={plan.featured ? 'isFeatured' : ''} key={plan.label}>
              {plan.featured ? <span className="templatesPlanBadge">Most popular</span> : null}
              <p className="templatesPlanLabel">{plan.label}</p>
              <h3>{plan.title}</h3>
              <p className="templatesPlanDescription">{plan.description}</p>
              <ul>{plan.features.map((feature) => <li key={feature}><span aria-hidden="true">✓</span>{feature}</li>)}</ul>
              <a className={`templatesButton ${plan.featured ? 'templatesButtonLight' : 'templatesButtonOutline'}`} href="#templates-contact">{plan.cta} <span aria-hidden="true">↗</span></a>
            </article>
          ))}
        </div>
        <div className="templatesShell templatesPaymentBar">
          <div>
            <span className="templatesPaymentIcon" aria-hidden="true">✓</span>
            <div><strong>Already have an approved scope?</strong><small>Pay securely for an authorized web development hour. Additional work only begins after written approval.</small></div>
          </div>
          <div className="templatesPaymentPrice"><strong>$150</strong><span>/ approved hour</span></div>
          <a href="https://checkout.cmjsoftware.com/b/bJeeVc99r1DZdbafJ4eAg0C" target="_blank" rel="noreferrer">Pay securely <span aria-hidden="true">↗</span></a>
        </div>
      </section>

      <section className="templatesFaq" id="templates-faq" aria-labelledby="templates-faq-title">
        <div className="templatesShell templatesFaqGrid">
          <div>
            <p className="templatesSectionLabel">Good to know</p>
            <h2 id="templates-faq-title">Questions,<br /><em>answered.</em></h2>
          </div>
          <div className="templatesFaqList">
            {faqItems.map((item, index) => (
              <details key={item.question} open={index === 0}>
                <summary>{item.question}<span aria-hidden="true">+</span></summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="templatesContact" id="templates-contact" aria-labelledby="templates-contact-title">
        <div className="templatesShell templatesContactGrid">
          <div className="templatesContactCopy">
            <p className="templatesSectionLabel">Start your website</p>
            <h2 id="templates-contact-title">Your next website can feel like your best location.</h2>
            <p>Tell us what you are building. CMJ Software will reply with the right next step, a realistic timeline, and a clear path to a fixed quote.</p>
            <a href="mailto:clintonjohnson@cmjsoftware.com">clintonjohnson@cmjsoftware.com <span aria-hidden="true">↗</span></a>
          </div>
          <form className="templatesContactForm" onSubmit={handleInquiry}>
            <div className="templatesFieldRow">
              <label>Name<input name="name" autoComplete="name" required /></label>
              <label>Email<input name="email" type="email" autoComplete="email" required /></label>
            </div>
            <div className="templatesFieldRow">
              <label>Business or brand<input name="company" autoComplete="organization" /></label>
              <label>Phone<input name="phone" type="tel" autoComplete="tel" /></label>
            </div>
            <div className="templatesFieldRow">
              <label>Best fit
                <select name="buildType" defaultValue="Business">
                  <option>Launch</option>
                  <option>Business</option>
                  <option>Commerce+</option>
                  <option>Not sure yet</option>
                </select>
              </label>
              <label>Ideal timing
                <select name="timeline" defaultValue="Within 1–2 months">
                  <option>As soon as possible</option>
                  <option>Within 1–2 months</option>
                  <option>Within 3–4 months</option>
                  <option>Flexible</option>
                </select>
              </label>
            </div>
            <label>Tell us about the website<textarea name="message" rows={5} placeholder="What does your business do, and what should the website help people do?" required /></label>
            <button className="templatesButton templatesButtonLight" type="submit" disabled={formState === 'sending'}>
              {formState === 'sending' ? 'Sending…' : 'Start my project'} <span aria-hidden="true">↗</span>
            </button>
            <p className={`templatesFormMessage is-${formState}`} aria-live="polite">{formMessage || 'No obligation. No surprise work. Just a clear first conversation.'}</p>
          </form>
        </div>
      </section>

      <footer className="templatesFooter">
        <div className="templatesShell">
          <a className="templatesFooterBrand" href="https://cmjsoftware.com/" target="_blank" rel="noreferrer">
            <img src="/cmj-software-logo.png" alt="CMJ Software" />
            <span><strong>CMJ Software</strong><small>Websites, software, and digital experiences.</small></span>
          </a>
          <div className="templatesFooterLinks">
            <a href="/">Moor Engineers</a>
            <a href="#templates-work">Selected work</a>
            <a href="#templates-contact">Start a project</a>
            <a href="https://cmjsoftware.com/" target="_blank" rel="noreferrer">CMJSoftware.com ↗</a>
          </div>
          <p>© {new Date().getFullYear()} CMJ Software. Built with care in Detroit.</p>
        </div>
      </footer>
    </main>
  );
}
